import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import Copyright from "components/partials/auth/copyright";
import Logo from "components/partials/auth/logo";
import { resetPasswordAction } from "action/auth-action";
import { routes } from "router";

const schema = z
  .object({
    password: z.string().min(6, "Password must be at least 6 characters."),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof schema>;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid");
  const token = searchParams.get("token");
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [passwordType, setPasswordType] = React.useState<"password" | "text">("password");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    if (!uid || !token) return;
    setLoading(true);
    setServerError(null);
    const response = await resetPasswordAction({ uid, token, newPassword: data.password ?? "" });
    setLoading(false);
    if (response.status === 200) setSuccess(true);
    else setServerError(response.message || "We could not reset your password.");
  };

  const invalidLink = !uid || !token;
  return (
    <main className="min-h-dvh bg-card">
      <div className="mx-auto flex min-h-dvh w-full max-w-[524px] flex-col px-7 py-10 md:px-[42px]">
        <Link to={routes.Login.path} className="inline-flex w-fit items-center gap-3">
          <Logo />
          <span className="text-lg font-semibold text-default-900">Storbolt</span>
        </Link>
        <section className="flex flex-1 flex-col justify-center">
          {success ? (
            <div>
              <h1 className="text-2xl font-medium text-default-900">Password updated</h1>
              <p className="mt-3 text-base leading-6 text-default-500">Your password has been reset. You can now sign in.</p>
              <Link to={routes.Login.path} className="mt-7 inline-block text-sm font-medium text-default-900 hover:underline">Sign in</Link>
            </div>
          ) : invalidLink ? (
            <div>
              <h1 className="text-2xl font-medium text-default-900">This link is incomplete</h1>
              <p className="mt-3 text-base leading-6 text-default-500">Request a new password reset link and use the most recent email.</p>
              <Link to={routes.ForgotPassword.path} className="mt-7 inline-block text-sm font-medium text-default-900 hover:underline">Request a new link</Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-medium text-default-900">Set a new password</h1>
              <p className="mt-3 text-base leading-6 text-default-500">Choose a password with at least 6 characters.</p>
              <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-medium text-default-600">New password</Label>
                  <Input id="password" type={passwordType} autoComplete="new-password" size="lg" aria-invalid={Boolean(errors.password)} {...register("password")} />
                  {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="font-medium text-default-600">Confirm new password</Label>
                  <Input id="confirmPassword" type={passwordType} autoComplete="new-password" size="lg" aria-invalid={Boolean(errors.confirmPassword)} {...register("confirmPassword")} />
                  {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>}
                </div>
                <button type="button" onClick={() => setPasswordType((type) => type === "password" ? "text" : "password")} className="text-sm font-medium text-default-600 hover:underline">
                  {passwordType === "password" ? "Show passwords" : "Hide passwords"}
                </button>
                {serverError && <p className="text-sm text-destructive">{serverError}</p>}
                <Button fullWidth disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? "Updating password..." : "Reset password"}
                </Button>
              </form>
            </>
          )}
        </section>
        <footer className="pt-10 text-center text-xs font-normal text-default-500"><Copyright /></footer>
      </div>
    </main>
  );
}
